import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  StyleSheet,
  FlatList,
  TouchableOpacity,
  TextInput,
} from "react-native";
import { Text, View, Card, Wrapper } from "@components/atoms/Themed";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import { OtherCategory, KeyboardId } from "@constants/App";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import Toast from "@components/atoms/Toast";
import InputAccessoryView from "@components/atoms/InputAccessoryView";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import { graphql, useFragment, useMutation } from "react-relay/hooks";
import Spacer from "@components/atoms/Spacer";
import Icon from "@components/atoms/Icon";
import { Style } from "@components/molecules/TextInput";
import _ from "lodash";
import GradientButton from "@components/atoms/GradientButton";
import { CategoryEditInfluencerCategories$key } from "@generated/CategoryEditInfluencerCategories.graphql";
import {
  CategoryEditMCategories$key,
  CategoryEditMCategories$data,
} from "@generated/CategoryEditMCategories.graphql";
import { CategoryEditUpdateMutation } from "@generated/CategoryEditUpdateMutation.graphql";
import Divider from "@components/atoms/Divider";

const categoryLinked = graphql`
  fragment CategoryEditInfluencerCategories on Influencer {
    influencerCategories {
      mCategoryId
      otherContent
      mCategoryKeyName
    }
  }
`;

const categoryList = graphql`
  fragment CategoryEditMCategories on Query {
    mCategories {
      id
      name
      keyName
    }
  }
`;

const updateQuery = graphql`
  mutation CategoryEditUpdateMutation(
    $input: UpdateInfluencerCategoriesMutationInput!
  ) {
    updateInfluencerCategories(input: $input) {
      __typename
      ... on Influencer {
        influencerCategories {
          mCategoryId
          categoryName
          otherContent
        }
      }
      ... on UpdateInfluencerCategoriesError {
        message
      }
    }
  }
`;

type Props = {
  categoryFragment: CategoryEditInfluencerCategories$key;
  listFragment: CategoryEditMCategories$key;
};

export default function CategoryEdit({
  categoryFragment,
  listFragment,
}: Props) {
  let flatListRef: FlatList | null = null;
  const { influencerCategories } = useFragment(
    categoryLinked,
    categoryFragment
  );
  const { mCategories } = useFragment(categoryList, listFragment);
  const initialCategories = useMemo(
    () => influencerCategories.map((row) => row.mCategoryId).sort(),
    [influencerCategories]
  );
  const otherCategoryId = useMemo(
    () =>
      _.head(mCategories.filter((row) => row.keyName === OtherCategory))?.id,
    [mCategories]
  );
  const otherCategory = useMemo(
    () =>
      _.head(
        influencerCategories.filter(
          (row) => row.mCategoryKeyName === OtherCategory
        )
      ),
    [influencerCategories]
  );
  const [selectedCategories, setCategories] =
    useState<string[]>(initialCategories);
  const [otherContent, setOtherContent] = useState<string | null>(
    otherCategory?.otherContent ?? null
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commit] = useMutation<CategoryEditUpdateMutation>(updateQuery);
  const [postable, setPostable] = useState<boolean>(false);

  useEffect(() => {
    const other = _.head(
      selectedCategories.filter((id) => id === otherCategoryId)
    );
    if (!_.isEmpty(other) && !_.isEmpty(otherContent)) {
      setPostable(otherCategory?.otherContent !== otherContent);
    }
  }, [otherContent, otherCategory, otherCategoryId, selectedCategories]);

  useEffect(() => {
    setPostable(!_.isEqual(selectedCategories, initialCategories));
  }, [selectedCategories, initialCategories]);

  const save = useCallback(async () => {
    if (
      otherCategoryId !== undefined &&
      selectedCategories.indexOf(otherCategoryId) !== -1 &&
      _.isEmpty(otherContent)
    ) {
      setError("その他カテゴリーを入力してください");
      return;
    }
    setLoading(true);
    await new Promise<void>((resolve) => {
      commit({
        variables: {
          input: {
            categories: selectedCategories,
            otherContent,
          },
        },
        onCompleted({ updateInfluencerCategories }) {
          if (
            updateInfluencerCategories.__typename ===
            "UpdateInfluencerCategoriesError"
          ) {
            setError(updateInfluencerCategories.message);
          } else {
            setMessage("保存しました");
          }
          resolve();
        },
      });
    });
    setLoading(false);
  }, [commit, otherCategoryId, otherContent, selectedCategories]);

  const toggle = useCallback(
    (data: CategoryEditMCategories$data["mCategories"][number]) => {
      const filteredCategories = selectedCategories.filter(
        (categoryId: string) => data.id !== categoryId
      );
      if (selectedCategories.length === filteredCategories.length) {
        setCategories((categories) => _.concat(categories, data.id).sort());
        if (data.keyName === OtherCategory) {
          setTimeout(() => flatListRef?.scrollToEnd({ animated: false }), 100);
        }
      } else {
        setCategories(filteredCategories);
      }
    },
    [flatListRef, selectedCategories]
  );

  return (
    <View style={styles.container}>
      <Spacer height={8} />
      <KeyboardAvoidingView>
        <FlatList
          ref={(ref) => {
            if (ref !== null && flatListRef === null) {
              flatListRef = ref;
            }
          }}
          data={mCategories}
          keyExtractor={(item) => item.keyName}
          ListFooterComponent={<Spacer height={80} />}
          renderItem={({ item }) => (
            <Wrapper>
              <TouchableOpacity onPress={() => toggle(item)}>
                <Card bordered style={styles.content}>
                  <View style={styles.label}>
                    <Text style={styles.name}>{item.name}</Text>
                  </View>

                  {selectedCategories.indexOf(item.id) !== -1 ? (
                    <View bordered style={[styles.icon, styles.active]}>
                      <Icon color={Colors.white} name="check" size={20} />
                    </View>
                  ) : (
                    <View bordered style={styles.icon}>
                      <Icon color={Colors.black} name="pluss" size={20} />
                    </View>
                  )}
                </Card>
              </TouchableOpacity>
              {item.keyName === OtherCategory &&
                selectedCategories.indexOf(item.id) !== -1 && (
                  <>
                    <Divider />
                    <Card style={styles.textField}>
                      <TextInput
                        inputAccessoryViewID={KeyboardId}
                        maxLength={10}
                        onChangeText={(value: string) => setOtherContent(value)}
                        placeholder="その他カテゴリーを入力"
                        style={Style.form}
                        value={otherContent ?? ""}
                      />
                    </Card>
                  </>
                )}
            </Wrapper>
          )}
        />
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!postable}
          loading={loading}
          onPress={save}
          title="保存する"
        />
      </Card>
      {message !== null && (
        <Toast message={message} onClose={() => setMessage(null)} />
      )}
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
      <InputAccessoryView nativeID={KeyboardId} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    flex: 1,
    flexDirection: "row",
    paddingHorizontal: 16,
    paddingVertical: 24,
    borderTopWidth: 1,
    borderStyle: "solid",
    alignItems: "center",
  },
  label: {
    flex: 1,
  },
  name: {
    ...Fonts.xlr100,
  },
  icon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 1,
    borderStyle: "solid",
    alignItems: "center",
    justifyContent: "center",
  },
  active: {
    backgroundColor: Colors.green,
  },
  textField: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
});
